import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine,
  ComposedChart, Line, AreaChart, Area
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video, BarChart2
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries, getRetentionReport,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, VT_LABEL, VT_FILTER_TABS
} from '../components/SharedUI';


// ═══════════════════════════════════════════════════════════════
// REPORTS PAGE
// ═══════════════════════════════════════════════════════════════
const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

function getLast6Months() {
  const out = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 10, padding: '10px 14px', fontFamily: 'Tajawal, sans-serif', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,.08)', zIndex: 100 }}>
      <div className="dark:!text-gray-100" style={{ fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.fill, marginBottom: 2 }}>
          {p.dataKey}: {formatIQD(p.value * 1000)} ( {formatUSD(p.value * 1000 / EXCHANGE_RATE)} )
        </div>
      ))}
    </div>
  );
};

const getOutflowIcon = (key) => {
  switch (key) {
    case 'salaries': return <Users size={16} color="#3B82F6" />;
    case 'team_leaders': return <Repeat size={16} color="#8B5CF6" />;
    case 'commissions': return <TrendingUp size={16} color="#EC4899" />;
    case 'ads_marketing': return <Target size={16} color="#F59E0B" />;
    case 'software_tools': return <CreditCard size={16} color="#10B981" />;
    default: return <Briefcase size={16} color="#6B7280" />;
  }
};

const formatSecondsDelivered = (secs) => {
  const s = Number(secs || 0);
  if (s < 60) return `${s} ثانية`;
  const m = (s / 60).toFixed(1);
  return `${s.toLocaleString()} ثانية (${m} دقيقة)`;
};

function printMonthlyReport(report, dateRange, catMap = {}) {
  let prodHtml = '';
  if (report.seconds_delivered) {
    const formatSecs = (secs) => {
      const s = Number(secs || 0);
      const m = (s / 60).toFixed(1);
      return `${s.toLocaleString()} ثانية (${m} دقيقة)`;
    };
    prodHtml = `
      <h3>🎬 ملخص إنتاج وتسليم الفيديو للفترة</h3>
      <table>
        <thead>
          <tr style="background:#F8FAFC;">
            <th style="padding:10px 12px;text-align:right;font-size:12px;color:#64748B;border-bottom:2px solid #E2E8F0;">نوع التسليم</th>
            <th style="padding:10px 12px;text-align:left;font-size:12px;color:#64748B;border-bottom:2px solid #E2E8F0;">حجم الإنتاج بالثواني والدقائق</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom:1px solid #F1F5F9;">
            <td style="padding:10px 12px;font-size:13px;">إجمالي حجم الإنتاج الكلي</td>
            <td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:left;">${formatSecs(report.seconds_delivered.total)}</td>
          </tr>
          <tr style="border-bottom:1px solid #F1F5F9;">
            <td style="padding:10px 12px;font-size:13px;">تسليمات باقات الاشتراكات</td>
            <td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:left;color:#10B981">${formatSecs(report.seconds_delivered.subscription)}</td>
          </tr>
          <tr style="border-bottom:1px solid #F1F5F9;">
            <td style="padding:10px 12px;font-size:13px;">تسليمات عابرة ومشاريع مستقلة</td>
            <td style="padding:10px 12px;font-size:13px;font-weight:700;text-align:left;color:#F59E0B">${formatSecs(report.seconds_delivered.transient)}</td>
          </tr>
        </tbody>
      </table>
    `;
  }

  let shareRows = '';
  if (report.expenses?.outflowBreakdown && report.expenses.outflowBreakdown.length > 0) {
    const totalOut = report.expenses.outflowBreakdown.reduce((s, i) => s + i.value, 0);
    shareRows = report.expenses.outflowBreakdown.map(item => {
      const pct = totalOut > 0 ? ((item.value / totalOut) * 100).toFixed(1) : '0.0';
      return `<tr>
        <td style="font-weight:600;"><span style="color:${item.color}">●</span> ${item.name}</td>
        <td style="font-weight:700;text-align:center;">${pct}%</td>
        <td style="text-align:left;font-weight:700;color:#E74C3C">${formatIQD(item.value)}</td>
      </tr>`;
    }).join('');
  }

  let expRows = (report.expenses?.breakdown || []).map(e => {
    const label = catMap[e.category]?.label || e.category;
    return `<tr><td>${label}</td><td style="text-align:left;font-weight:700;color:#E74C3C">${formatIQD(e.total || 0)}</td></tr>`;
  }).join('');

  if (report.expenses?.payroll > 0) {
    expRows += `<tr><td>رواتب الموظفين</td><td style="text-align:left;font-weight:700;color:#E74C3C">${formatIQD(report.expenses.payroll)}</td></tr>`;
  }

  const projRows = (report.project_profits || []).map(p =>
    `<tr><td>${p.title}</td><td>${p.client_name}</td><td style="color:#17A589">${formatIQD(p.revenue || 0)}</td><td style="color:#E74C3C">${formatIQD(p.cost || 0)}</td><td style="font-weight:800;color:${p.profit >= 0 ? '#9B8EC4' : '#E74C3C'}">${formatIQD(p.profit || 0)}</td><td>${p.margin}%</td></tr>`
  ).join('');

  const subRows = (report.subscriptions || []).map(s =>
    `<tr><td>${s.client_name}</td><td>${s.package_name}</td><td>${s.period_start} إلى ${s.period_end}</td><td style="font-weight:700;">${formatIQD(s.price_iqd || 0)}</td><td>${s.total_seconds}ث (${(s.total_seconds/60).toFixed(1)}د)</td><td style="color:#17A589;font-weight:700;">${s.seconds_consumed_in_period}ث (${(s.seconds_consumed_in_period/60).toFixed(1)}د)</td><td style="color:#E74C3C;font-weight:700;">${s.remaining_seconds}ث (${(s.remaining_seconds/60).toFixed(1)}د)</td></tr>`
  ).join('');

  const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>كشف حساب مالي - ${dateRange.from} إلى ${dateRange.to}</title>
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;600;700;800&display=swap" rel="stylesheet">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Tajawal',sans-serif; background:#fff; color:#0F1E33; padding:40px; max-width:900px; margin:0 auto; }
  h1 { font-size:26px; font-weight:800; color:#9B8EC4; margin-bottom:4px; }
  .sub { color:#64748B; font-size:14px; margin-bottom:30px; }
  .kpi-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-bottom:30px; }
  .kpi { background:#F8FAFC; border-radius:10px; padding:12px 14px; border:1px solid #E2E8F0; }
  .kpi-label { font-size:10px; color:#94A3B8; font-weight:600; margin-bottom:6px; }
  .kpi-value { font-size:14px; font-weight:800; }
  table { width:100%; border-collapse:collapse; margin-bottom:24px; }
  th { background:#F8FAFC; padding:10px 12px; text-align:right; font-size:12px; color:#64748B; border-bottom:2px solid #E2E8F0; }
  td { padding:10px 12px; font-size:13px; border-bottom:1px solid #F1F5F9; }
  h3 { font-size:15px; font-weight:700; margin:24px 0 12px; color:#0F1E33; border-bottom: 2px solid #F0ECF7; padding-bottom: 5px; }
  .footer { text-align:center; margin-top:40px; padding-top:16px; border-top:1px solid #E2E8F0; color:#94A3B8; font-size:12px; }
  @media print { body { padding:20px; } }
</style>
</head>
<body>
<h1>📊 كشف الحساب المالي للفترة</h1>
<div class="sub">MS MEDIA — الفترة: من <strong>${dateRange.from}</strong> إلى <strong>${dateRange.to}</strong></div>
<div class="kpi-grid">
  <div class="kpi"><div class="kpi-label">الرصيد الافتتاحي</div><div class="kpi-value" style="color:#64748B">${formatIQD(report.previous_month_balance || 0)}</div></div>
  <div class="kpi"><div class="kpi-label">الإيرادات</div><div class="kpi-value" style="color:#17A589">+${formatIQD(report.revenue || 0)}</div></div>
  <div class="kpi"><div class="kpi-label">المصاريف الكلية</div><div class="kpi-value" style="color:#E74C3C">-${formatIQD(report.expenses?.total || 0)}</div></div>
  <div class="kpi"><div class="kpi-label">صافي الربح</div><div class="kpi-value" style="color:#9B8EC4">${formatIQD(report.profit || 0)}</div></div>
  <div class="kpi"><div class="kpi-label">الرصيد الختامي</div><div class="kpi-value" style="color:#10B981">${formatIQD(report.total_cash || 0)}</div></div>
</div>
${prodHtml}
${shareRows ? `<h3>📊 كشف توزيع حصص النفقات والمدفوعات (حصص الشركاء والموظفين)</h3><table><thead><tr><th>التصنيف</th><th style="text-align:center;">النسبة</th><th style="text-align:left;">المبلغ بالدينار</th></tr></thead><tbody>${shareRows}</tbody></table>` : ''}
${expRows ? `<h3>كشف تفصيل المصاريف</h3><table><thead><tr><th>التصنيف</th><th>المبلغ</th></tr></thead><tbody>${expRows}</tbody></table>` : ''}
${subRows ? `<h3>كشف استهلاك الباقات والاشتراكات</h3><table><thead><tr><th>العميل</th><th>الباقة</th><th>الفترة</th><th>القيمة</th><th>إجمالي الباقة</th><th>المستهلك بالفترة</th><th>المتبقي الكلي</th></tr></thead><tbody>${subRows}</tbody></table>` : ''}
${projRows ? `<h3>كشف ربحية المشاريع</h3><table><thead><tr><th>المشروع</th><th>العميل</th><th>الإيراد</th><th>التكلفة</th><th>الربح</th><th>الهامش</th></tr></thead><tbody>${projRows}</tbody></table>` : ''}
<div class="footer">تم إصدار هذا التقرير بتاريخ ${new Date().toLocaleDateString('ar-IQ')} — نظام إدارة MS MEDIA</div>
</body>
</html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 800);
}

function exportCSV(invoices) {
  const headers = ['رقم الفاتورة', 'العميل', 'المشروع', 'التاريخ', 'الإجمالي IQD', 'المدفوع IQD', 'المتبقي IQD', 'الحالة'];
  const rows = invoices.map(i => [
    i.invoice_number, i.client_name, i.project_title || '—',
    i.issue_date, i.total_iqd, i.paid_iqd,
    (i.total_iqd - i.paid_iqd), i.payment_status
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'invoice-statement.csv'; a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [filterType, setFilterType] = useState('month'); // 'month' or 'custom'
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));

  const [customFrom, setCustomFrom] = useState(() => {
    const monthStr = new Date().toISOString().substring(0, 7);
    return monthStr + '-01';
  });
  const [customTo, setCustomTo] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return now.toISOString().substring(0, 7) + '-' + String(lastDay).padStart(2, '0');
  });

  const dateRange = useMemo(() => {
    if (filterType === 'month') {
      const start = selectedMonth + '-01';
      const d1 = new Date(start);
      const lastDay = new Date(d1.getFullYear(), d1.getMonth() + 1, 0).getDate();
      const end = selectedMonth + '-' + String(lastDay).padStart(2, '0');
      return { from: start, to: end };
    } else {
      return { from: customFrom, to: customTo };
    }
  }, [filterType, selectedMonth, customFrom, customTo]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ltvData, setLtvData] = useState([]);
  const [overdueData, setOverdueData] = useState([]);
  const [activeTab, setActiveTab] = useState('monthly');
  const [trendData, setTrendData] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(false);

  // Invoice Statement state
  const [stmtFilters, setStmtFilters] = useState({ from: '', to: '', status: '', paid_only: false });
  const [stmtData, setStmtData] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtClients, setStmtClients] = useState([]);

  // Daily Reports state
  const [dailyFilter, setDailyFilter] = useState(() => {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
    return { from: start, to: end };
  });
  const [dailySales, setDailySales] = useState(null);
  const [dailyDeliveries, setDailyDeliveries] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  // Retention state
  const [retentionMonth, setRetentionMonth] = useState(new Date().toISOString().substring(0, 7));
  const [retentionData, setRetentionData] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);

  // Cash flow forecast memo
  const cashFlowData = useMemo(() => {
    if (!report) return [];
    const startCash = report.total_cash || 0;
    const points = [{
      date: 'اليوم',
      'السيولة المتوقعة': Math.round(startCash / 1000)
    }];
    let currentCumulative = startCash;
    
    // Sort and map future invoices
    const sortedFuture = [...(report.future_invoices || [])].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    sortedFuture.forEach(item => {
      currentCumulative += (item.outstanding_iqd || 0);
      points.push({
        date: item.due_date,
        'السيولة المتوقعة': Math.round(currentCumulative / 1000)
      });
    });
    return points;
  }, [report]);

  useEffect(() => {
    getExpenseCategories().then(cats => {
      setCategories(cats);
      const cm = {};
      cats.forEach(c => { cm[c.key] = c; });
      setCatMap(cm);
    }).catch(() => { });
  }, []);

  // Synchronize daily and retention filters with global dateRange
  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      setDailyFilter({ from: dateRange.from, to: dateRange.to });
      setRetentionMonth(dateRange.from.substring(0, 7));
    }
  }, [dateRange]);

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    setLoading(true);
    getMonthlyReport({ from: dateRange.from, to: dateRange.to })
      .then(r => { setReport(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateRange]);

  useEffect(() => {
    if (activeTab === 'ltv') getClientLTV().then(setLtvData);
    if (activeTab === 'overdue') getOverdueInvoices().then(setOverdueData);
    if (activeTab === 'statement') getClients().then(setStmtClients);
    if (activeTab === 'trend') {
      setLoadingTrend(true);
      const months = getLast6Months();
      Promise.all(months.map(m => getMonthlyReport(m))).then(results => {
        setTrendData(results.map((r, i) => ({
          month: MONTHS_AR[parseInt(months[i].split('-')[1]) - 1],
          إيرادات: Math.round((r.revenue || 0) / 1000),
          مصاريف: Math.round((r.expenses?.total || 0) / 1000),
          ربح: Math.round((r.profit || 0) / 1000),
        })));
        setLoadingTrend(false);
      }).catch(() => setLoadingTrend(false));
    }
  }, [activeTab]); // eslint-disable-line

  const loadDaily = async (e) => {
    if (e) e.preventDefault();
    setDailyLoading(true);
    try {
      if (activeTab === 'daily-sales') {
        const data = await getDailySales(dailyFilter);
        setDailySales(data);
      } else if (activeTab === 'daily-deliveries') {
        const data = await getDailyDeliveries(dailyFilter);
        setDailyDeliveries(data);
      }
    } catch { toast.error('خطأ في تحميل التقرير اليومي'); }
    finally { setDailyLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'daily-sales' || activeTab === 'daily-deliveries') {
      loadDaily();
    }
  }, [activeTab, dailyFilter.from, dailyFilter.to]);

  const loadRetention = async (e) => {
    if (e) e.preventDefault();
    setRetentionLoading(true);
    try {
      const data = await getRetentionReport({ month: retentionMonth });
      setRetentionData(data);
    } catch { toast.error('خطأ في تحميل التقرير'); }
    finally { setRetentionLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'retention') loadRetention();
  }, [activeTab, retentionMonth]);

  const handleStmtSearch = async (e) => {
    e?.preventDefault();
    setStmtLoading(true);
    try {
      const params = {};
      if (stmtFilters.from) params.from = stmtFilters.from;
      if (stmtFilters.to) params.to = stmtFilters.to;
      if (stmtFilters.status) params.status = stmtFilters.status;
      if (stmtFilters.client_id) params.client_id = stmtFilters.client_id;
      if (stmtFilters.paid_only) params.paid_only = 'true';
      const data = await getInvoiceStatement(params);
      setStmtData(data);
    } catch { toast.error('خطأ في تحميل الكشف'); }
    finally { setStmtLoading(false); }
  };

  const TABS = [
    { id: 'monthly', label: 'كشف الحساب والتقرير المالي', icon: <Calendar size={14} /> },
    { id: 'trend', label: 'الاتجاه 6 أشهر', icon: <TrendingUp size={14} /> },
    { id: 'daily-sales', label: 'المبيعات اليومية', icon: <DollarSign size={14} /> },
    { id: 'daily-deliveries', label: 'التسليمات اليومية', icon: <Video size={14} /> },
    { id: 'retention', label: 'ولاء العملاء', icon: <UserCheck size={14} /> },
    { id: 'ltv', label: 'قيمة العملاء', icon: <Users size={14} /> },
    { id: 'overdue', label: 'الفواتير المتأخرة', icon: <AlertCircle size={14} /> },
    { id: 'statement', label: 'كشف الفواتير', icon: <FileText size={14} /> },
  ];

  const overdueTotal = overdueData.reduce((s, i) => s + Math.max((i.total_iqd || 0) - (i.paid_iqd || 0), 0), 0);

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart2 size={22} color="#F59E0B" /> التقارير والتحليلات
        </h1>
      </div>

      {/* ── Unified Date Filter Bar ── */}
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        background: 'var(--bg-flat-white, white)', borderRadius: 16, padding: '16px 20px',
        border: '1px solid var(--border-light, #E2E8F0)', marginBottom: 20,
        boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: 'var(--bg-flat-100, #F1F5F9)', borderRadius: 10, padding: 3 }}>
            <button onClick={() => setFilterType('month')} style={{
              padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, fontFamily: 'Tajawal, sans-serif', transition: 'all 0.2s',
              background: filterType === 'month' ? 'var(--bg-flat-white, white)' : 'transparent',
              color: filterType === 'month' ? '#F59E0B' : 'var(--text-gray, #64748B)',
              boxShadow: filterType === 'month' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
            }}>📅 فلترة بالشهور</button>
            <button onClick={() => setFilterType('custom')} style={{
              padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, fontFamily: 'Tajawal, sans-serif', transition: 'all 0.2s',
              background: filterType === 'custom' ? 'var(--bg-flat-white, white)' : 'transparent',
              color: filterType === 'custom' ? '#F59E0B' : 'var(--text-gray, #64748B)',
              boxShadow: filterType === 'custom' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
            }}>🗓️ فترة مخصصة</button>
          </div>

          {filterType === 'month' ? (
            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 13, fontFamily: 'Tajawal, sans-serif', color: 'var(--text-dark-s, #374151)', outline: 'none', background: 'var(--bg-flat-white, white)' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 13, fontFamily: 'Tajawal, sans-serif', color: 'var(--text-dark-s, #374151)', outline: 'none', background: 'var(--bg-flat-white, white)' }} />
              <span style={{ color: 'var(--text-muted, #94A3B8)', fontSize: 13 }}>إلى</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 13, fontFamily: 'Tajawal, sans-serif', color: 'var(--text-dark-s, #374151)', outline: 'none', background: 'var(--bg-flat-white, white)' }} />
            </div>
          )}
        </div>

        {report && (
          <button onClick={() => printMonthlyReport(report, dateRange, catMap)} style={btn('#F59E0B', 'white')}>
            🖨️ تصدير كشف حساب PDF
          </button>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '2px solid var(--border-light, #E2E8F0)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, fontFamily: 'Tajawal, sans-serif', marginBottom: -2,
            color: activeTab === t.id ? '#F59E0B' : 'var(--text-gray, #64748B)',
            borderBottom: `3px solid ${activeTab === t.id ? '#F59E0B' : 'transparent'}`,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {t.icon} {t.label}
            {t.id === 'overdue' && overdueData.length > 0 && (
              <span style={{ ...pill('#FEE2E2', '#E74C3C'), fontSize: 10, padding: '1px 6px' }}>{overdueData.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════ MONTHLY TAB ════════════════════ */}
      {activeTab === 'monthly' && (
        loading
          ? <div className="dark:!text-gray-400" style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted, #94A3B8)', fontSize: 14 }}>جاري التحميل…</div>
          : report && (
            <div>
              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
                <StatCard label={filterType === 'month' ? "رصيد الشهر السابق" : "الرصيد الافتتاحي للفترة"} iqd={report.previous_month_balance} color="var(--text-gray, #64748B)" icon={<TrendingUp />} />
                <StatCard label="الإيرادات المحصلة" iqd={report.revenue} color="#10B981" icon={<TrendingUp />} />
                <StatCard label="المصاريف والرواتب" iqd={report.expenses?.total} color="#EF4444" icon={<TrendingDown />} />
                <StatCard label="صافي الربح" iqd={report.profit} color={report.profit >= 0 ? '#8B5CF6' : '#EF4444'} icon={<DollarSign />} />
                <StatCard label="هامش الربح للفترة" rawValue={`${report.margin || 0}%`}
                  color={report.margin >= 60 ? '#10B981' : report.margin >= 30 ? '#F59E0B' : '#EF4444'}
                  sub={report.margin >= 60 ? 'ممتاز ✓' : report.margin >= 30 ? 'جيد' : 'منخفض'} />
                <StatCard label={filterType === 'month' ? "إجمالي الكاش بالشركة" : "الرصيد الختامي للفترة"} iqd={report.total_cash} color={report.total_cash >= 0 ? '#10B981' : '#EF4444'} icon={<DollarSign />} />
              </div>
              
              {/* Production and Deliveries Summary */}
              {report.seconds_delivered && (
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
                  ...S.cardPad,
                  marginBottom: 20,
                  border: '1px solid var(--border-light, #E2E8F0)'
                }}>
                  <h3 style={{ ...S.sectionTitle, borderBottom: '1.5px solid var(--border-light, #F1F5F9)', paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                    <Video size={18} color="#8B5CF6" /> ملخص إنتاج وتسليم الفيديو 🎬
                  </h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted, #94A3B8)', marginTop: 4, marginBottom: 14 }}>
                    يوضح إجمالي حجم العمل المنجز في صناعة وتوصيل الفيديوهات خلال الفترة المحددة، بما في ذلك الفيديوهات للعملاء العابرين والمشاريع بدون اشتراك نشط.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                    {/* Card 1: Total */}
                    <div style={{ background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 12, padding: '12px 16px' }} className="dark:!bg-white/5 dark:!border-white/5">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', fontWeight: 700 }} className="dark:!text-gray-400">إجمالي الإنتاج الكلي</span>
                        <span style={{ width: 24, height: 24, borderRadius: 6, background: '#8B5CF615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Video size={14} color="#8B5CF6" />
                        </span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--text-dark-h, #0F1E33)' }} className="dark:!text-gray-100">
                        {formatSecondsDelivered(report.seconds_delivered.total)}
                      </div>
                      <p style={{ fontSize: 10, color: 'var(--text-muted, #94A3B8)', marginTop: 2, margin: 0 }}>
                        جميع الفيديوهات والطلبات المسلّمة بالنظام
                      </p>
                    </div>

                    {/* Card 2: Subscriptions */}
                    <div style={{ background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 12, padding: '12px 16px' }} className="dark:!bg-white/5 dark:!border-white/5">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', fontWeight: 700 }} className="dark:!text-gray-400">تسليمات باقات الاشتراكات</span>
                        <span style={{ width: 24, height: 24, borderRadius: 6, background: '#10B98115', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Package size={14} color="#10B981" />
                        </span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--text-dark-h, #0F1E33)' }} className="dark:!text-gray-100">
                        {formatSecondsDelivered(report.seconds_delivered.subscription)}
                      </div>
                      <p style={{ fontSize: 10, color: 'var(--text-muted, #94A3B8)', marginTop: 2, margin: 0 }}>
                        مخصومة تلقائياً من رصيد باقات العملاء
                      </p>
                    </div>

                    {/* Card 3: Transient */}
                    <div style={{ background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 12, padding: '12px 16px' }} className="dark:!bg-white/5 dark:!border-white/5">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', fontWeight: 700 }} className="dark:!text-gray-400">تسليمات عابرة ومشاريع مستقلة</span>
                        <span style={{ width: 24, height: 24, borderRadius: 6, background: '#F59E0B15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Target size={14} color="#F59E0B" />
                        </span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--text-dark-h, #0F1E33)' }} className="dark:!text-gray-100">
                        {formatSecondsDelivered(report.seconds_delivered.transient)}
                      </div>
                      <p style={{ fontSize: 10, color: 'var(--text-muted, #94A3B8)', marginTop: 2, margin: 0 }}>
                        تسليمات لعملاء عابرين ومشاريع بدون باقة نشطة
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Outflow Share Analysis Card */}
              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginBottom: 20 }}>
                <h3 style={{ ...S.sectionTitle, borderBottom: '1.5px solid var(--border-light, #F1F5F9)', paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PieChartIcon size={18} color="#F59E0B" /> تحليل حصص ونسب توزيع النفقات والمدفوعات
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 24, marginTop: 14 }} className="grid-cols-1 md:grid-cols-2">
                  {/* Left Column: Share details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(report.expenses?.outflowBreakdown || []).map(item => {
                      const totalOutflow = (report.expenses?.outflowBreakdown || []).reduce((sum, i) => sum + i.value, 0);
                      const pct = totalOutflow > 0 ? ((item.value / totalOutflow) * 100) : 0;
                      
                      return (
                        <div key={item.key} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          background: 'var(--bg-flat-50, #F8FAFC)',
                          border: '1px solid var(--border-light, #E2E8F0)',
                          borderRadius: 12,
                          padding: '12px 16px',
                          position: 'relative'
                        }} className="dark:!bg-white/5 dark:!border-white/5">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: `${item.color}15`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                {getOutflowIcon(item.key)}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 750, color: 'var(--text-dark-h, #0F1E33)' }} className="dark:!text-gray-150">{item.name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {pct >= 25 && (
                                <span style={{ ...pill('#FEE2E2', '#E74C3C'), fontSize: 10, fontWeight: 700 }}>حصة مرتفعة 🔥</span>
                              )}
                              <span style={{ ...pill(`${item.color}15`, item.color), fontSize: 11, fontWeight: 800 }}>
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: '#E74C3C' }}>{formatIQD(item.value)}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>
                              ≈ {formatUSD(item.value / EXCHANGE_RATE)}
                            </span>
                          </div>

                          <div style={{ width: '100%', height: 5, background: 'var(--border-light, #E2E8F0)', borderRadius: 2.5, overflow: 'hidden' }} className="dark:!bg-white/10">
                            <div style={{ width: `${pct}%`, height: '100%', background: item.color, borderRadius: 2.5 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right Column: Recharts Pie Chart */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 16, padding: 16 }} className="dark:!bg-white/5 dark:!border-white/5">
                    <div style={{ position: 'relative', width: '100%', height: 210 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={(report.expenses?.outflowBreakdown || []).filter(i => i.value > 0)}
                            cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" stroke="none"
                          >
                            {(report.expenses?.outflowBreakdown || []).filter(i => i.value > 0).map((entry, idx) => (
                              <Cell key={idx} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={v => [formatIQD(v), '']}
                            contentStyle={{ backgroundColor: 'var(--bg-flat-white, white)', borderColor: 'var(--border-light, #E2E8F0)', borderRadius: '10px', color: 'var(--text-dark-h, #0F1E33)', fontFamily: 'Tajawal, sans-serif', fontSize: 12 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                        <div style={{ fontSize: 9, color: 'var(--text-muted, #94A3B8)', fontWeight: 600 }}>إجمالي المدفوعات</div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: '#E74C3C', marginTop: 2 }}>
                          {formatIQD((report.expenses?.outflowBreakdown || []).reduce((s, i) => s + i.value, 0))}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginTop: 14, width: '100%' }}>
                      {(report.expenses?.outflowBreakdown || []).filter(i => i.value > 0).map(item => (
                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--text-dark-s, #374151)' }} className="dark:!text-gray-300">
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Raw Expense breakdown list */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 18 }}>
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.cardPad}>
                  <h3 style={S.sectionTitle}>تفصيل بنود المصاريف بالفترة (التصنيفات المخصصة)</h3>
                  {(report.expenses?.breakdown?.length > 0 || report.expenses?.payroll > 0)
                    ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                        {report.expenses.breakdown.map(e => (
                          <div key={e.category} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 10 }} className="dark:!bg-white/5 dark:!border-white/5">
                            <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600 }}>
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: catMap[e.category]?.color || 'var(--text-muted, #94A3B8)', display: 'inline-block', flexShrink: 0 }} />
                              {catMap[e.category]?.label || e.category}
                            </span>
                            <Dual iqd={e.total} />
                          </div>
                        ))}
                        {report.expenses.payroll > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-flat-50, #F8FAFC)', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 10 }} className="dark:!bg-white/5 dark:!border-white/5">
                            <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600 }}>
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#3B82F6', display: 'inline-block' }} />
                              رواتب الموظفين
                            </span>
                            <Dual iqd={report.expenses.payroll} />
                          </div>
                        )}
                      </div>
                    )
                    : <p className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontSize: 13, margin: 0 }}>لا توجد مصاريف في هذه الفترة</p>
                  }
                </div>
              </div>

              {/* Subscriptions Statement */}
              {report.subscriptions?.length > 0 && (
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginBottom: 16 }}>
                  <h3 style={S.sectionTitle}>كشف استهلاك الباقات والاشتراكات للفترة</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                          {['العميل', 'الباقة', 'الفترة', 'قيمة الباقة', 'إجمالي الباقة', 'المستهلك بالفترة', 'المتبقي الكلي', 'الحالة'].map(h => <th key={h} style={S.th}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {report.subscriptions.map((s, i) => (
                          <tr key={s.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                            <td style={td({ fontWeight: 600 })}>{s.client_name}</td>
                            <td style={td({ fontWeight: 600, color: '#9B8EC4' })}>{s.package_name}</td>
                            <td style={td({ color: 'var(--text-gray, #64748B)', fontSize: 12 })}>{s.period_start} إلى {s.period_end}</td>
                            <td style={td()}><Dual iqd={s.price_iqd} /></td>
                            <td style={td({ fontWeight: 600 })}>{s.total_seconds} ثانية ({(s.total_seconds / 60).toFixed(1)} دقيقة)</td>
                            <td style={td({ color: '#10B981', fontWeight: 700 })}>{s.seconds_consumed_in_period} ثانية ({(s.seconds_consumed_in_period / 60).toFixed(1)} دقيقة)</td>
                            <td style={td({ color: '#EF4444', fontWeight: 700 })}>{s.remaining_seconds} ثانية ({(s.remaining_seconds / 60).toFixed(1)} دقيقة)</td>
                            <td style={td()}>
                              <span style={pill(
                                s.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                                s.status === 'active' ? '#065F46' : '#991B1B'
                              )}>
                                {s.status === 'active' ? 'نشط' : 'مغلق'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Project profitability */}
              {report.project_profits?.length > 0 && (
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginBottom: 16 }}>
                  <h3 style={S.sectionTitle}>ربحية المشاريع المكتملة للفترة</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                          {['المشروع', 'العميل', 'الإيراد', 'التكلفة', 'الربح', 'الهامش'].map(h => <th key={h} style={S.th}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {report.project_profits.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                            <td style={td({ fontWeight: 600 })}>{p.title}</td>
                            <td style={td({ color: 'var(--text-gray, #64748B)' })}>{p.client_name}</td>
                            <td style={td()}><Dual iqd={p.revenue} /></td>
                            <td style={td()}><Dual iqd={p.cost} redIqd /></td>
                            <td style={td()}><Dual iqd={p.profit} bold redIqd={p.profit < 0} /></td>
                            <td style={td()}>
                              <span style={pill(
                                p.margin >= 50 ? '#D1FAE5' : p.margin >= 20 ? '#FEF3C7' : '#FEE2E2',
                                p.margin >= 50 ? '#065F46' : p.margin >= 20 ? '#92400E' : '#991B1B'
                              )}>
                                {p.margin}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Cash Flow Forecast Chart */}
              {cashFlowData.length > 1 && (
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginBottom: 16 }}>
                  <h3 style={S.sectionTitle}>توقعات التدفق النقدي والسيولة المستقبلية (بالألف د.ع)</h3>
                  <div style={{ position: 'relative', height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cashFlowData}>
                        <defs>
                          <linearGradient id="colorCash" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light, #F1F5F9)" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, fill: 'var(--text-gray, #64748B)' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontFamily: 'Tajawal, sans-serif', fontSize: 10, fill: 'var(--text-muted, #94A3B8)' }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={v => [formatIQD(v * 1000), 'السيولة المتوقعة']} contentStyle={{ backgroundColor: 'var(--bg-flat-white, white)', borderColor: 'var(--border-light, #E2E8F0)', borderRadius: '10px', fontFamily: 'Tajawal, sans-serif', fontSize: 12 }} />
                        <Area type="monotone" dataKey="السيولة المتوقعة" stroke="#10B981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorCash)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Bottom stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <StatCard label="عملاء جدد في هذه الفترة" rawValue={`${report.new_clients || 0} عميل`} color="#8B5CF6" />
                <StatCard label="الفواتير الصادرة بالفترة" rawValue={`${report.invoices?.count || 0} فاتورة`}
                  iqd={report.invoices?.total} color="#8B5CF6" />
              </div>
            </div>
          )
      )}

      {/* ════════════════════ TREND TAB ════════════════════ */}
      {activeTab === 'trend' && (
        <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.cardPad}>
          <h3 style={S.sectionTitle}>الإيرادات والمصاريف والربح — آخر 6 أشهر (بالألف دينار)</h3>
          {loadingTrend
            ? <div className="dark:!text-gray-400" style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted, #94A3B8)' }}>جاري تحميل البيانات…</div>
            : (
              <>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={trendData} barCategoryGap="25%" barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light, #F1F5F9)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, fill: 'var(--text-gray, #64748B)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, fill: 'var(--text-muted, #94A3B8)' }} axisLine={false} tickLine={false} />
                    <ReferenceLine y={0} stroke="var(--border-light, #E2E8F0)" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, paddingTop: 8 }} />
                    <Bar dataKey="إيرادات" fill="#10B981" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="مصاريف" fill="#EF4444" radius={[5, 5, 0, 0]} />
                    <Line type="monotone" dataKey="ربح" stroke="#8B5CF6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
                {/* Totals row */}
                {trendData.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 20 }}>
                    {[
                      { l: 'إجمالي الإيرادات (6 أشهر)', v: trendData.reduce((s, d) => s + d['إيرادات'], 0), c: '#17A589' },
                      { l: 'إجمالي المصاريف (6 أشهر)', v: trendData.reduce((s, d) => s + d['مصاريف'], 0), c: '#E74C3C' },
                      { l: 'إجمالي الربح (6 أشهر)', v: trendData.reduce((s, d) => s + d['ربح'], 0), c: '#9B8EC4' },
                    ].map(x => (
                      <div key={x.l} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 10, padding: '12px 16px' }}>
                        <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginBottom: 4 }}>{x.l}</div>
                        <div style={{ fontWeight: 800, color: x.c, fontSize: 16 }}>{formatIQD(x.v * 1000)}</div>
                        <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>≈ {formatUSD(x.v * 1000 / EXCHANGE_RATE)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )
          }
        </div>
      )}

      {/* ════════════════════ LTV TAB ════════════════════ */}
      {activeTab === 'ltv' && (
        <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                  {['#', 'العميل', 'الحالة', 'إجمالي العقود', 'المدفوع', 'المتبقي', 'مشاريع', 'اشتراكات'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ltvData.map((c, i) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                    <td style={td({ color: 'var(--text-muted, #94A3B8)', fontSize: 12 })}>{i + 1}</td>
                    <td style={td({ fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)' })}>{c.name}</td>
                    <td style={td()}>
                      <span style={pill(c.status === 'active' ? '#D1FAE5' : '#FEE2E2', c.status === 'active' ? '#065F46' : '#991B1B')}>
                        {c.status === 'active' ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td style={td()}><Dual iqd={c.lifetime_value_iqd} bold /></td>
                    <td style={td()}><Dual iqd={c.total_paid_iqd} /></td>
                    <td style={td()}><Dual iqd={c.outstanding_iqd} redIqd={c.outstanding_iqd > 0} bold={c.outstanding_iqd > 0} /></td>
                    <td style={td({ textAlign: 'center', fontWeight: 600 })}>{c.project_count}</td>
                    <td style={td({ textAlign: 'center', fontWeight: 600 })}>{c.subscription_count}</td>
                  </tr>
                ))}
                {ltvData.length === 0 && <EmptyRow cols={8} msg="لا توجد بيانات" />}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════════════ OVERDUE TAB ════════════════════ */}
      {activeTab === 'overdue' && (
        <div>
          {overdueData.length > 0 && (
            <div style={{
              marginBottom: 16, padding: '14px 18px', background: '#FEF2F2',
              borderRadius: 10, border: '1px solid #FECACA',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color="#E74C3C" />
                <span style={{ fontSize: 14, color: '#991B1B', fontWeight: 700 }}>
                  {overdueData.length} فاتورة متأخرة
                </span>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#E74C3C' }}>{formatIQD(overdueTotal)}</div>
                <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{formatUSD(iqd2usd(overdueTotal))}</div>
              </div>
            </div>
          )}
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                    {['رقم الفاتورة', 'العميل', 'الهاتف', 'الإجمالي', 'المتبقي', 'الاستحقاق', 'أيام التأخر'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overdueData.map((inv, i) => {
                    const remain = (inv.total_iqd || 0) - (inv.paid_iqd || 0);
                    const days = Math.round(inv.days_overdue || 0);
                    return (
                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                        <td style={td({ fontWeight: 700, color: '#9B8EC4' })}>{inv.invoice_number}</td>
                        <td style={td({ fontWeight: 600 })}>{inv.client_name}</td>
                        <td style={td({ color: 'var(--text-gray, #64748B)' })}>
                          {inv.client_phone ? (() => {
                            const textMsg = encodeURIComponent(`مرحباً ${inv.client_name}،\nنود تذكيركم بوجود فاتورة متأخرة:\n\n📄 الفاتورة: ${inv.invoice_number}\n⏳ المتبقي: ${formatIQD(remain)}\n⚠️ أيام التأخير: ${days} يوم\n\nتاريخ الاستحقاق: ${inv.due_date}\n\nشكراً لتعاملك معنا — MS MEDIA`);
                            return (
                              <a href={'https://wa.me/' + inv.client_phone.replace(/\\D/g, '') + '?text=' + textMsg} target="_blank" rel="noreferrer"
                                style={{ color: '#4ADE80', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 13 }}>
                                📱 {inv.client_phone}
                              </a>
                            );
                          })() : '—'}
                        </td>
                        <td style={td()}><Dual iqd={inv.total_iqd} usd={inv.total_usd} /></td>
                        <td style={td()}><Dual iqd={remain} redIqd bold /></td>
                        <td style={td({ color: '#E74C3C', fontWeight: 600 })}>{inv.due_date}</td>
                        <td style={td()}>
                          <span style={pill(
                            days > 60 ? '#FEE2E2' : days > 30 ? '#FEF3C7' : '#FEF9C3',
                            days > 60 ? '#991B1B' : days > 30 ? '#92400E' : '#713F12'
                          )}>
                            {days} يوم
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {overdueData.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 48, textAlign: 'center', color: '#17A589', fontSize: 14, fontWeight: 600 }}>
                        ✓ لا توجد فواتير متأخرة
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Statement Tab ── */}
      {activeTab === 'statement' && (
        <div>
          {/* Filters */}
          <form onSubmit={handleStmtSearch} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
            display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end',
            background: 'var(--bg-flat-white, white)', borderRadius: 12, border: '1px solid var(--border-light, #E2E8F0)',
            padding: '16px 18px', marginBottom: 16
          }}>
            <div style={{ flex: '1 1 130px' }}>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>من تاريخ</div>
              <input type="date" value={stmtFilters.from}
                onChange={e => setStmtFilters(f => ({ ...f, from: e.target.value }))}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>إلى تاريخ</div>
              <input type="date" value={stmtFilters.to}
                onChange={e => setStmtFilters(f => ({ ...f, to: e.target.value }))}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>العميل</div>
              <select value={stmtFilters.client_id || ''}
                onChange={e => setStmtFilters(f => ({ ...f, client_id: e.target.value }))}
                style={{ ...S.input, width: '100%' }}>
                <option value="">الكل</option>
                {stmtClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>الحالة</div>
              <select value={stmtFilters.status}
                onChange={e => setStmtFilters(f => ({ ...f, status: e.target.value, paid_only: false }))}
                style={{ ...S.input, width: '100%' }}>
                <option value="">الكل</option>
                <option value="unpaid">غير مدفوعة</option>
                <option value="partial">جزئي</option>
                <option value="paid">مدفوعة</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 2 }}>
              <input type="checkbox" id="paidOnly" checked={stmtFilters.paid_only}
                onChange={e => setStmtFilters(f => ({ ...f, paid_only: e.target.checked, status: '' }))} />
              <label htmlFor="paidOnly" className="dark:!text-gray-100" style={{ fontSize: 13, color: 'var(--text-dark-s, #374151)', cursor: 'pointer' }}>مدفوعة فقط</label>
            </div>
            <button type="submit" disabled={stmtLoading}
              style={{ ...btn('#9B8EC4'), padding: '9px 22px', alignSelf: 'flex-end' }}>
              {stmtLoading ? '...' : '🔍 بحث'}
            </button>
          </form>

          {/* Export buttons */}
          {stmtData && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => exportCSV(stmtData.invoices)} style={btn('#17A589')}>📊 CSV</button>
              <button onClick={() => window.print()} style={btn('var(--text-gray, #64748B)')}>🖨️ طباعة</button>
            </div>
          )}

          {/* Summary bar */}
          {stmtData && (
            <div style={{
              display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap'
            }}>
              {[
                { label: 'إجمالي الفواتير', val: stmtData.totalIssued, color: '#9B8EC4' },
                { label: 'إجمالي المدفوع', val: stmtData.totalPaid, color: '#17A589' },
                { label: 'إجمالي المتبقي', val: stmtData.totalOutstanding, color: '#E74C3C' },
              ].map(item => (
                <div key={item.label} style={{
                  flex: '1 1 160px', background: 'var(--bg-flat-white, white)', borderRadius: 10,
                  border: `1.5px solid ${item.color}22`, padding: '12px 16px'
                }}>
                  <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: item.color }}>{formatIQD(item.val)}</div>
                  <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{formatUSD(iqd2usd(item.val))}</div>
                </div>
              ))}
              <div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{
                flex: '1 1 100px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 10,
                border: '1px solid var(--border-light, #E2E8F0)', padding: '12px 16px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
              }}>
                <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 2 }}>عدد الفواتير</div>
                <div className="dark:!text-gray-100" style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)' }}>{stmtData.count}</div>
              </div>
            </div>
          )}

          {/* Results table */}
          {stmtData && (
            <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                      {['رقم الفاتورة', 'العميل', 'المشروع', 'التاريخ', 'الإجمالي', 'المدفوع', 'المتبقي', 'الحالة'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stmtData.invoices.map((inv, i) => {
                      const remain = (inv.total_iqd || 0) - (inv.paid_iqd || 0);
                      const statusCfg = {
                        paid: { bg: '#D1FAE5', color: '#065F46', label: 'مدفوعة' },
                        partial: { bg: '#FEF3C7', color: '#92400E', label: 'جزئي' },
                        unpaid: { bg: '#FEE2E2', color: '#991B1B', label: 'غير مدفوعة' },
                      }[inv.payment_status] || { bg: '#F1F5F9', color: 'var(--text-dark-s, #374151)', label: inv.payment_status };
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                          <td style={td({ fontWeight: 700, color: '#9B8EC4' })}>{inv.invoice_number}</td>
                          <td style={td({ fontWeight: 600 })}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <span>{inv.client_name || '—'}</span>
                              {inv.client_phone && (() => {
                                const textMsg = encodeURIComponent(`مرحباً ${inv.client_name}،\nإليك تفاصيل الفاتورة الخاصة بك:\n\n📄 رقم الفاتورة: ${inv.invoice_number}\n💰 الإجمالي: ${formatIQD(inv.total_iqd)}\n✅ المدفوع: ${formatIQD(inv.paid_iqd)}\n⏳ المتبقي: ${formatIQD(remain)}\n\nتاريخ الاستحقاق: ${inv.due_date || '—'}\n\nشكراً لتعاملك معنا — MS MEDIA`);
                                return (
                                  <a href={'https://wa.me/' + inv.client_phone.replace(/\\D/g, '') + '?text=' + textMsg} target="_blank" rel="noreferrer"
                                    style={{ color: '#4ADE80', textDecoration: 'none', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                                    📱 {inv.client_phone}
                                  </a>
                                );
                              })()}
                            </div>
                          </td>
                          <td style={td({ color: 'var(--text-gray, #64748B)' })}>{inv.project_title || '—'}</td>
                          <td style={td({ color: 'var(--text-gray, #64748B)' })}>{inv.issue_date}</td>
                          <td style={td()}><Dual iqd={inv.total_iqd} usd={inv.total_usd} /></td>
                          <td style={td()}><Dual iqd={inv.paid_iqd} /></td>
                          <td style={td()}><Dual iqd={remain} redIqd={remain > 0} bold={remain > 0} /></td>
                          <td style={td()}>
                            <span style={pill(statusCfg.bg, statusCfg.color)}>{statusCfg.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                    {stmtData.invoices.length === 0 && (
                      <tr>
                        <td colSpan={8} className="dark:!text-gray-400" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted, #94A3B8)', fontSize: 14 }}>
                          لا توجد فواتير تطابق الفلتر
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!stmtData && !stmtLoading && (
            <div className="dark:!text-gray-400" style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted, #94A3B8)', fontSize: 14 }}>
              اختر الفلاتر واضغط بحث لعرض الكشف
            </div>
          )}
        </div>
      )}

      {/* ── Daily Sales Tab ── */}
      {activeTab === 'daily-sales' && (
        <div>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', background: 'var(--bg-flat-white, white)', padding: '16px', borderRadius: 12, border: '1px solid var(--border-light, #E2E8F0)' }}>
            <div>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>من تاريخ</div>
              <input type="date" value={dailyFilter.from} onChange={e => setDailyFilter(f => ({ ...f, from: e.target.value }))} style={S.input} />
            </div>
            <div>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>إلى تاريخ</div>
              <input type="date" value={dailyFilter.to} onChange={e => setDailyFilter(f => ({ ...f, to: e.target.value }))} style={S.input} />
            </div>
            <button onClick={loadDaily} disabled={dailyLoading} style={{ ...btn('#17A589'), padding: '9px 22px' }}>
              {dailyLoading ? '...' : 'بحث'}
            </button>
          </div>

          {dailySales && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                <StatCard label="إجمالي المبيعات" iqd={dailySales.total_iqd} color="#17A589" icon={<DollarSign />} />
                <StatCard label="عدد الدفعات" rawValue={dailySales.count} color="#3B82F6" icon={<CheckCircle />} />
              </div>
              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                        <th style={S.th}>التاريخ</th>
                        <th style={S.th}>عدد الدفعات</th>
                        <th style={S.th}>الإجمالي (IQD)</th>
                        <th style={S.th}>الإجمالي (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailySales.daily.map((d, i) => (
                        <tr key={d.pay_date} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                          <td style={td({ fontWeight: 700 })}>{d.pay_date}</td>
                          <td style={td()}>{d.count}</td>
                          <td style={td({ color: '#17A589', fontWeight: 600 })}>{formatIQD(d.total_iqd)}</td>
                          <td style={td({ color: 'var(--text-gray, #64748B)' })}>{formatUSD(d.total_usd)}</td>
                        </tr>
                      ))}
                      {dailySales.daily.length === 0 && <EmptyRow cols={4} msg="لا توجد مبيعات في هذه الفترة" />}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Daily Deliveries Tab ── */}
      {activeTab === 'daily-deliveries' && (
        <div>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', background: 'var(--bg-flat-white, white)', padding: '16px', borderRadius: 12, border: '1px solid var(--border-light, #E2E8F0)' }}>
            <div>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>من تاريخ</div>
              <input type="date" value={dailyFilter.from} onChange={e => setDailyFilter(f => ({ ...f, from: e.target.value }))} style={S.input} />
            </div>
            <div>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>إلى تاريخ</div>
              <input type="date" value={dailyFilter.to} onChange={e => setDailyFilter(f => ({ ...f, to: e.target.value }))} style={S.input} />
            </div>
            <button onClick={loadDaily} disabled={dailyLoading} style={{ ...btn('#8B5CF6'), padding: '9px 22px' }}>
              {dailyLoading ? '...' : 'بحث'}
            </button>
          </div>

          {dailyDeliveries && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                <StatCard label="إجمالي الثواني المسلمة" rawValue={`${dailyDeliveries.total_seconds} ثانية`} sub={`≈ ${(dailyDeliveries.total_seconds / 60).toFixed(1)} دقيقة`} color="#8B5CF6" icon={<Video />} />
                <StatCard label="إجمالي عدد التسليمات" rawValue={dailyDeliveries.count} color="#3B82F6" icon={<CheckCircle />} />
              </div>
              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                        <th style={S.th}>التاريخ</th>
                        <th style={S.th}>عدد التسليمات</th>
                        <th style={S.th}>عدد العملاء المعنيين</th>
                        <th style={S.th}>الثواني المسلمة</th>
                        <th style={S.th}>بالدقائق</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyDeliveries.daily.map((d, i) => (
                        <tr key={d.delivery_date} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                          <td style={td({ fontWeight: 700 })}>{d.delivery_date}</td>
                          <td style={td()}>{d.count}</td>
                          <td style={td()}>{d.clients_count}</td>
                          <td style={td({ color: '#8B5CF6', fontWeight: 600 })}>{d.total_seconds} ثانية</td>
                          <td style={td({ color: 'var(--text-gray, #64748B)' })}>{(d.total_seconds / 60).toFixed(1)} دقيقة</td>
                        </tr>
                      ))}
                      {dailyDeliveries.daily.length === 0 && <EmptyRow cols={5} msg="لا توجد تسليمات في هذه الفترة" />}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ════════════════════ RETENTION TAB ════════════════════ */}
      {activeTab === 'retention' && (
        <div>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', background: 'var(--bg-flat-white, white)', padding: '16px', borderRadius: 12, border: '1px solid var(--border-light, #E2E8F0)' }}>
            <div>
              <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4, fontWeight: 600 }}>اختر الشهر</div>
              <input type="month" value={retentionMonth} onChange={e => setRetentionMonth(e.target.value)} style={S.input} />
            </div>
            <button onClick={loadRetention} disabled={retentionLoading} style={{ ...btn('#10B981'), padding: '9px 22px' }}>
              {retentionLoading ? '...' : 'تحديث'}
            </button>
          </div>

          {retentionData && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                <StatCard label="نسبة الاحتفاظ بالعملاء (CRR)" rawValue={`${retentionData.retention_rate}%`} color={retentionData.retention_rate >= 80 ? '#10B981' : retentionData.retention_rate >= 50 ? '#F59E0B' : '#EF4444'} icon={<TrendingUp />} />
                <StatCard label="نسبة التسرب (Churn Rate)" rawValue={`${retentionData.churn_rate}%`} color={retentionData.churn_rate < 10 ? '#10B981' : retentionData.churn_rate < 30 ? '#F59E0B' : '#EF4444'} icon={<TrendingDown />} />
                <StatCard label="عملاء في بداية الشهر" rawValue={retentionData.metrics?.CS} color="#6366F1" icon={<Users />} />
                <StatCard label="عملاء جدد هذا الشهر" rawValue={retentionData.metrics?.CN} color="#EC4899" icon={<Plus />} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Retained / Active Clients */}
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                  <h3 style={{ ...S.sectionTitle, color: '#10B981' }}>العملاء النشطون والمستمرون ({retentionData.lists?.retained?.length || 0})</h3>
                  <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                          <th style={S.th}>العميل</th>
                          <th style={S.th}>النوع</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(retentionData.lists?.retained || []).map((c, i) => (
                          <tr key={c.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                            <td style={td({ fontWeight: 600 })}>{c.name}</td>
                            <td style={td()}>
                              <span style={pill(c.type === 'returning' ? '#DBEAFE' : '#D1FAE5', c.type === 'returning' ? '#1D4ED8' : '#047857')}>
                                {c.type === 'returning' ? 'متجدد (عاد)' : 'مستمر'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {(retentionData.lists?.retained || []).length === 0 && <EmptyRow cols={2} msg="لا يوجد عملاء مستمرون" />}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Churned Clients */}
                <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                  <h3 style={{ ...S.sectionTitle, color: '#EF4444' }}>العملاء المغادرون (التسرب) ({retentionData.lists?.churned?.length || 0})</h3>
                  <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                          <th style={S.th}>العميل</th>
                          <th style={S.th}>تاريخ الانقطاع</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(retentionData.lists?.churned || []).map((c, i) => (
                          <tr key={c.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                            <td style={td({ fontWeight: 600 })}>{c.name}</td>
                            <td style={td({ color: '#EF4444', fontWeight: 600 })}>{c.last_active_date || 'غير معروف'}</td>
                          </tr>
                        ))}
                        {(retentionData.lists?.churned || []).length === 0 && <EmptyRow cols={2} msg="لا يوجد عملاء مغادرون" />}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
